import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ToastMessage, type } from "@/lib/toastr";
import { cn } from "@/lib/utils";
import { changePassword } from "@/services/Auth.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { EyeClosed, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import { z } from "zod";
import Logo from "../../assets/texpress.png";
import { useState } from "react";
import {
  ConfirmPasswordError,
  PasswordError,
} from "@/components/PasswordError";
import { VscEye } from "react-icons/vsc";

export const formSchema = z.object({
  newPassword: z.string().nonempty(),
  confirmPassword: z.string().nonempty(),
});
const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [show2, setShow2] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "all",
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const mutation = useMutation({
    mutationFn: (v) => changePassword(v),
    mutationKey: ["resetPassword"],
    onSuccess: (data) => {
      navigate("/reset-successful", {
        state: {
          status: true,
        },
      });
      // ToastMessage(type.SUCCESS, data?.data?.message);
    },
    onError: (err: any) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const dataReset: any = {
      code: location?.state?.code,
      email: location?.state?.email,
      password: data?.newPassword,
    };
    // setMail(data?.email);
    mutation.mutate(dataReset);
  };

  return (
    <div className="flex flex-col items-center justify-center gap-6 min-h-svh bg-muted ">
      <div className="flex flex-col items-center gap-6">
        <Link to="/">
          <img src={Logo} className="h-10" />
        </Link>
        <Card className="w-full lg:w-[448px] p-6">
          <CardHeader className="text-center lg:w-[350px] mx-auto">
            <CardTitle className="text-2xl font-medium">
              Enter new password
            </CardTitle>
            <CardDescription className="">
              Reset your password by generating a new secure password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid gap-6">
                  <FormField
                    control={form.control}
                    name="newPassword"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <div
                            className={cn(
                              "flex w-full items-center gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14",
                              form.formState.errors?.newPassword === undefined
                                ? "border-input-border"
                                : "border-destructive"
                            )}
                          >
                            <div className="flex flex-col w-full">
                              <FormLabel className="text-xs font-normal">
                                New Password
                              </FormLabel>

                              <FormControl>
                                <Input
                                  type={!show ? "password" : "text"}
                                  placeholder=""
                                  className="w-full h-6 px-0 py-0 border-none outline-none placeholder:py-0 placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                                  {...field}
                                />
                              </FormControl>
                            </div>
                            <div
                              className=" left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
                              onClick={() => setShow(!show)}
                            >
                              {!show ? (
                                <EyeClosed />
                              ) : (
                                <VscEye className="w-5 h-5" />
                              )}
                            </div>
                          </div>
                        </FormItem>
                      );
                    }}
                  />
                  <PasswordError
                    passwordErrorLowerCaseChar={form.watch("newPassword")}
                    passwordErrorMin={form.watch("newPassword")}
                    passwordErrorUpperCaseChar={form.watch("newPassword")}
                    passwordErrorHasNumber={form.watch("newPassword")}
                    passwordErrorSpecialChar={form.watch("newPassword")}
                  />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <div
                            className={cn(
                              "flex w-full items-center gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14",
                              form.formState.errors?.confirmPassword ===
                                undefined
                                ? "border-input-border"
                                : "border-destructive"
                            )}
                          >
                            <div className="flex flex-col w-full">
                              <FormLabel className="text-xs font-normal">
                                Confirm New Password
                              </FormLabel>

                              <FormControl>
                                <Input
                                  type={!show2 ? "password" : "text"}
                                  placeholder=""
                                  className="w-full h-6 px-0 py-0 border-none outline-none placeholder:py-0 placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                                  {...field}
                                />
                              </FormControl>
                            </div>
                            <div
                              className=" left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
                              onClick={() => setShow2(!show2)}
                            >
                              {!show2 ? (
                                <EyeClosed />
                              ) : (
                                <VscEye className="w-5 h-5" />
                              )}
                            </div>
                          </div>
                        </FormItem>
                      );
                    }}
                  />
                  <ConfirmPasswordError
                    password={form.watch("newPassword")}
                    confirmNewPassword={form.watch("confirmPassword")}
                  />

                  <Button
                    type="submit"
                    variant="default"
                    className="w-full bg-gradient-to-r from-primary to-primary-2 h-14 rounded-[10px] p-[10px]"
                    disabled={
                      !form.formState.isDirty ||
                      !form.formState.isValid ||
                      // form.getValues("otp") == "" ||
                      form.formState.isSubmitting ||
                      mutation.isPending
                    }
                  >
                    {mutation.isPending && (
                      <Loader2 className="text-white animate-spin" />
                    )}
                    Submit
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
        {/* <div className="grid gap-4 text-sm font-medium text-center text-primary w-full">
          <Link
            to="/"
            className="flex items-center justify-center text-center border-[#DAD3DA] border-[1px] h-[56px] w-full p-[10px] rounded-[10px]"
          >
            Remember your login access? Log in
          </Link>
        </div> */}
      </div>
    </div>
  );
};

export default ResetPassword;